import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  CheckCircle2, Loader2, XCircle, ArrowRight, RefreshCw,
  FileCode, Globe, AlertTriangle
} from 'lucide-react';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import useBuildStream from '@/hooks/useBuildStream';
import { buildSrcDoc } from '@/lib/buildSrcDoc';
import ViewportToggle from './ViewportToggle';

/* ─── Helpers ─── */

function formatDuration(ms) {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatElapsed(ms) {
  const secs = Math.floor(ms / 1000);
  const mins = Math.floor(secs / 60);
  const remaining = secs % 60;
  if (mins > 0) return `${mins}:${String(remaining).padStart(2, '0')}`;
  return `${remaining}s`;
}

/* ─── Step grouping by page ─── */

const GLOBAL_STEP_IDS = new Set([
  'load_design_context', 'auto_generate_images', 'prepare_prompts',
  'generate_layout', 'inject_fonts', 'save_db', 'generate_mock',
]);

function groupStepsByPage(steps) {
  const setup = [];
  const teardown = [];
  const batchSteps = [];
  const pageMap = new Map(); // pageName → { steps: [], sections: [] }

  // Separate global steps from batch steps
  let firstBatchSeen = false;
  let lastBatchIdx = -1;

  for (let i = 0; i < steps.length; i++) {
    if (steps[i].id.startsWith('batch_')) {
      firstBatchSeen = true;
      lastBatchIdx = i;
    }
  }

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (GLOBAL_STEP_IDS.has(step.id)) {
      if (!firstBatchSeen || i < steps.findIndex(s => s.id.startsWith('batch_'))) {
        setup.push(step);
      } else {
        teardown.push(step);
      }
    } else if (step.id.startsWith('batch_')) {
      batchSteps.push(step);
    } else {
      // Unknown step — put in setup
      setup.push(step);
    }
  }

  // Parse batch labels to group by page
  let isMultiPage = false;

  for (const step of batchSteps) {
    const colonIdx = step.label.indexOf(': ');
    let pageName;
    let sectionNames;

    if (colonIdx > 0) {
      // Multi-page format: "Home: Hero + Features"
      pageName = step.label.substring(0, colonIdx);
      sectionNames = step.label.substring(colonIdx + 2);
      isMultiPage = true;
    } else {
      // Single-page format: "Generating Hero + Features"
      pageName = '_single';
      sectionNames = step.label.replace(/^Generating\s+/i, '');
    }

    if (!pageMap.has(pageName)) {
      pageMap.set(pageName, { pageName, steps: [], sections: [] });
    }
    const pageGroup = pageMap.get(pageName);
    pageGroup.steps.push(step);

    // Parse individual section names
    const names = sectionNames.split(' + ').map(n => n.trim()).filter(Boolean);
    for (const name of names) {
      pageGroup.sections.push({ name, status: step.status });
    }
  }

  // Only consider multi-page if we have 2+ distinct pages
  if (pageMap.size <= 1) isMultiPage = false;

  return {
    isMultiPage,
    setup,
    pages: Array.from(pageMap.values()),
    teardown,
  };
}

function getGroupStatus(steps) {
  if (steps.some(s => s.status === 'error')) return 'error';
  if (steps.some(s => s.status === 'running')) return 'running';
  if (steps.length > 0 && steps.every(s => s.status === 'done')) return 'done';
  return 'pending';
}

/* ─── Sub-components ─── */

function StepIcon({ status }) {
  switch (status) {
    case 'done':
      return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
    case 'running':
      return (
        <div className="w-4 h-4 flex items-center justify-center shrink-0">
          <OrbisSpinner size={14} color="var(--orbis-blue)" />
        </div>
      );
    case 'error':
      return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
    default:
      return <div className="w-4 h-4 rounded-full border-2 border-[var(--orbis-s3)] shrink-0" />;
  }
}

function TabStatusDot({ status }) {
  switch (status) {
    case 'done':
      return <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />;
    case 'running':
      return <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--orbis-blue)', boxShadow: '0 0 4px var(--orbis-blue)' }} />;
    case 'error':
      return <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />;
    default:
      return <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--orbis-s3)' }} />;
  }
}

function LiveTimer({ startTime }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);
  return <span className="text-[var(--orbis-t4)] text-xs tabular-nums">{formatDuration(now - startTime)}</span>;
}

function StepRow({ step, thinkingMessages }) {
  return (
    <div
      className={`flex items-start gap-3 py-2 px-3 rounded-lg transition-colors ${
        step.status === 'running' ? 'bg-[rgba(184,204,223,0.05)]' : ''
      }`}
    >
      <div className="mt-0.5">
        <StepIcon status={step.status} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm font-medium truncate ${
            step.status === 'done' ? 'text-[var(--orbis-t3)]' :
            step.status === 'running' ? 'text-[var(--orbis-t1)]' :
            step.status === 'error' ? 'text-red-400' :
            'text-[var(--orbis-t4)]'
          }`}>
            {step.label}
          </span>
          {step.status === 'done' && step.duration_ms != null && (
            <span className="text-[var(--orbis-t4)] text-xs tabular-nums shrink-0">
              {formatDuration(step.duration_ms)}
            </span>
          )}
          {step.status === 'running' && step.startTime && (
            <LiveTimer startTime={step.startTime} />
          )}
        </div>
        {step.status === 'running' && (
          <div className="mt-1 space-y-0.5">
            {thinkingMessages
              .filter(t => t.stepId === step.id)
              .slice(-3)
              .map((t, i, arr) => (
                <p key={t.timestamp} className={`text-xs ${
                  i === arr.length - 1 ? 'text-[var(--orbis-blue)]' : 'text-[var(--orbis-t4)]'
                }`}>
                  {t.message}
                </p>
              ))
            }
            {thinkingMessages.filter(t => t.stepId === step.id).length === 0 && step.description && (
              <p className="text-xs text-[var(--orbis-t4)]">{step.description}</p>
            )}
          </div>
        )}
        {step.status === 'done' && (
          (() => {
            const lastThinking = thinkingMessages.filter(t => t.stepId === step.id).slice(-1)[0];
            return lastThinking ? (
              <p className="text-xs text-[var(--orbis-t4)] mt-0.5">{lastThinking.message}</p>
            ) : null;
          })()
        )}
        {step.status === 'error' && step.errorMsg && (
          <p className="text-xs text-red-400/80 mt-0.5">{step.errorMsg}</p>
        )}
      </div>
    </div>
  );
}

function SectionProgressGrid({ sections }) {
  if (!sections || sections.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 px-3 py-3 rounded-lg mb-2" style={{ background: 'var(--orbis-s1)' }}>
      {sections.map((sec, i) => (
        <div key={`${sec.name}-${i}`} className="flex items-center gap-1.5">
          {sec.status === 'done' && <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />}
          {sec.status === 'running' && (
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--orbis-blue)', boxShadow: '0 0 4px var(--orbis-blue)' }} />
          )}
          {sec.status === 'error' && <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />}
          {sec.status === 'pending' && <span className="w-2 h-2 rounded-full shrink-0" style={{ border: '1px solid var(--orbis-s3)' }} />}
          <span style={{
            fontSize: 12, fontFamily: 'var(--orbis-font)',
            color: sec.status === 'done' ? 'var(--orbis-t3)' :
                   sec.status === 'running' ? 'var(--orbis-t1)' :
                   sec.status === 'error' ? '#f87171' : 'var(--orbis-t4)',
          }}>
            {sec.name}
          </span>
        </div>
      ))}
    </div>
  );
}

function PreviewSkeleton() {
  return (
    <div className="flex-1 flex items-center justify-center bg-[var(--orbis-s1)] rounded-lg border border-[var(--orbis-s2)] m-4">
      <div className="text-center space-y-4 px-8">
        <div className="w-16 h-16 rounded-full bg-[var(--orbis-s2)] flex items-center justify-center mx-auto">
          <Globe className="w-8 h-8 text-[var(--orbis-t4)]" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-48 bg-[var(--orbis-s2)] rounded animate-pulse mx-auto" />
          <div className="h-3 w-36 bg-[var(--orbis-s2)]/60 rounded animate-pulse mx-auto" />
        </div>
        <p className="text-[var(--orbis-t4)] text-sm">Waiting for first page...</p>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */

export default function BuildStage({
  projectId,
  alreadyCompleted,
  existingCode,
  existingPages,
  generationMode,
  structure,
  onBuildComplete,
  onComplete
}) {
  const {
    steps, thinkingMessages, previewPages, isBuilding, isComplete, error,
    magicState, elapsedMs, startBuild, abortBuild
  } = useBuildStream(projectId);

  const [currentPreviewSlug, setCurrentPreviewSlug] = useState('home');
  const [hasStarted, setHasStarted] = useState(false);
  const [viewport, setViewport] = useState('desktop');
  const [activeTab, setActiveTab] = useState('setup');
  const [userPinned, setUserPinned] = useState(false);
  const scrollRef = useRef(null);

  // Group steps by page
  const grouped = useMemo(() => groupStepsByPage(steps), [steps]);

  // Auto-start build on mount if not already completed
  useEffect(() => {
    if (!alreadyCompleted && !hasStarted && !isBuilding) {
      setHasStarted(true);
      startBuild();
    }
  }, [alreadyCompleted, hasStarted, isBuilding, startBuild]);

  // Sync final magic state to parent when build completes
  useEffect(() => {
    if (isComplete && magicState && onBuildComplete) {
      onBuildComplete(magicState);
    }
  }, [isComplete, magicState, onBuildComplete]);

  // Auto-advance active tab to the group with a running step
  useEffect(() => {
    if (userPinned || !grouped.isMultiPage) return;

    // Check teardown first
    if (getGroupStatus(grouped.teardown) === 'running') {
      setActiveTab('finalize');
      return;
    }
    // Check pages
    for (const page of grouped.pages) {
      if (getGroupStatus(page.steps) === 'running') {
        setActiveTab(page.pageName);
        return;
      }
    }
    // Check setup
    if (getGroupStatus(grouped.setup) === 'running') {
      setActiveTab('setup');
    }
  }, [steps, grouped, userPinned]);

  // Auto-scroll log to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [steps, activeTab]);

  // If already completed, show the completion state directly
  if (alreadyCompleted && !hasStarted) {
    const pages = existingPages || [];
    const code = existingCode || {};
    const fileEntries = generationMode === 'multi_page'
      ? pages.map(p => ({ name: `${p.slug === 'home' ? 'index' : p.slug}.html`, size: (p.html?.length || 0) }))
      : [{ name: 'index.html', size: code.html?.length || 0 }];

    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-xl font-normal text-[var(--orbis-t1)]">Build Complete</h2>
          <p className="text-[var(--orbis-t3)]">Your website has been generated and is ready for review.</p>
        </div>
        <div className="bg-[var(--orbis-s1)] rounded-lg border border-[var(--orbis-s2)] p-4 w-full max-w-md">
          <p className="text-xs text-[var(--orbis-t4)] uppercase tracking-wider mb-3">Generated Files</p>
          {fileEntries.map((f, i) => (
            <div key={i} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <FileCode className="w-4 h-4 text-[var(--orbis-t4)]" />
                <span className="text-sm text-[var(--orbis-t3)]">{f.name}</span>
              </div>
              <span className="text-xs text-[var(--orbis-t4)]">{(f.size / 1024).toFixed(1)} KB</span>
            </div>
          ))}
        </div>
        <Button
          onClick={onComplete}
          className="bg-[var(--orbis-blue)] text-[var(--orbis-bg)] hover:bg-[var(--orbis-blue)]/80"
          data-testid="review-website-btn"
        >
          Review Website <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  // Calculate progress
  const completedSteps = steps.filter(s => s.status === 'done').length;
  const totalSteps = steps.length > 0 ? (steps[0].totalSteps || steps.length) : 1;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  // Get current preview HTML
  const currentPreview = previewPages.find(p => p.slug === currentPreviewSlug) || previewPages[0];
  const previewSrc = currentPreview ? buildSrcDoc(currentPreview.html, currentPreview.css, currentPreview.js) : '';

  return (
    <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 64px)', background: 'var(--surface-0)' }}>
      <div className="flex-1 flex gap-0 min-h-0">

        {/* ═══ Left panel — Agent Log ═══ */}
        <div className="w-[480px] shrink-0 flex flex-col bg-[var(--orbis-bg)]" style={{ borderRight: '1px solid #27272A60' }}>
          {/* Header */}
          <div className="border-b border-[var(--orbis-s2)]" style={{ padding: '16px 20px' }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--orbis-blue)' }}>
                <OrbisMarkSVG size={16} />
              </div>
              <div>
                <h3 className="text-sm font-normal text-[var(--orbis-t1)]">
                  {isComplete ? 'Build Complete' : isBuilding ? 'Building Your Website' : 'Ready to Build'}
                </h3>
                <p className="text-xs text-[var(--orbis-t4)]">
                  {isBuilding && `Elapsed: ${formatElapsed(elapsedMs)}`}
                  {isComplete && `Completed in ${formatElapsed(elapsedMs)}`}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[var(--orbis-t4)]">{completedSteps}/{totalSteps} steps</span>
                <span className="text-xs text-[var(--orbis-t4)]">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-1.5" />
            </div>
          </div>

          {/* Steps — tabbed (multi-page) or flat list */}
          {grouped.isMultiPage ? (
            <Tabs
              value={activeTab}
              onValueChange={(val) => { setActiveTab(val); setUserPinned(true); }}
              className="flex flex-col flex-1 min-h-0"
            >
              {/* Tab bar */}
              <TabsList className="h-auto bg-transparent rounded-none border-b border-[var(--orbis-s2)] px-3 py-2 gap-1 justify-start flex-wrap">
                <TabsTrigger
                  value="setup"
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-[var(--orbis-s1)] data-[state=active]:text-[var(--orbis-t1)] text-[var(--orbis-t4)] bg-transparent"
                  style={{ fontFamily: 'var(--orbis-font)' }}
                >
                  <TabStatusDot status={getGroupStatus(grouped.setup)} />
                  Setup
                </TabsTrigger>
                {grouped.pages.map((page) => (
                  <TabsTrigger
                    key={page.pageName}
                    value={page.pageName}
                    className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-[var(--orbis-s1)] data-[state=active]:text-[var(--orbis-t1)] text-[var(--orbis-t4)] bg-transparent"
                    style={{ fontFamily: 'var(--orbis-font)' }}
                  >
                    <TabStatusDot status={getGroupStatus(page.steps)} />
                    {page.pageName}
                  </TabsTrigger>
                ))}
                {grouped.teardown.length > 0 && (
                  <TabsTrigger
                    value="finalize"
                    className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-[var(--orbis-s1)] data-[state=active]:text-[var(--orbis-t1)] text-[var(--orbis-t4)] bg-transparent"
                    style={{ fontFamily: 'var(--orbis-font)' }}
                  >
                    <TabStatusDot status={getGroupStatus(grouped.teardown)} />
                    Finalize
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Tab content — scrollable */}
              <div ref={scrollRef} className="flex-1 overflow-auto" style={{ padding: '12px 20px' }}>
                <TabsContent value="setup" className="mt-0 space-y-1">
                  {grouped.setup.map(step => (
                    <StepRow key={step.id} step={step} thinkingMessages={thinkingMessages} />
                  ))}
                  {grouped.setup.length === 0 && (
                    <p className="text-xs text-[var(--orbis-t4)] py-4">Waiting for build to start...</p>
                  )}
                </TabsContent>

                {grouped.pages.map((page) => (
                  <TabsContent key={page.pageName} value={page.pageName} className="mt-0 space-y-1">
                    <SectionProgressGrid sections={page.sections} />
                    {page.steps.map(step => (
                      <StepRow key={step.id} step={step} thinkingMessages={thinkingMessages} />
                    ))}
                  </TabsContent>
                ))}

                <TabsContent value="finalize" className="mt-0 space-y-1">
                  {grouped.teardown.map(step => (
                    <StepRow key={step.id} step={step} thinkingMessages={thinkingMessages} />
                  ))}
                  {grouped.teardown.length === 0 && (
                    <p className="text-xs text-[var(--orbis-t4)] py-4">Waiting for section builds to complete...</p>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          ) : (
            /* Flat step list (single-page / single_pass) */
            <div ref={scrollRef} className="flex-1 overflow-auto space-y-1" style={{ padding: '12px 20px' }}>
              {steps.map((step) => (
                <StepRow key={step.id} step={step} thinkingMessages={thinkingMessages} />
              ))}
              {steps.length === 0 && isBuilding && (
                <div className="flex items-center gap-3 py-4">
                  <OrbisSpinner size={16} color="var(--orbis-blue)" />
                  <span className="text-sm text-[var(--orbis-t3)]">Connecting to build server...</span>
                </div>
              )}
            </div>
          )}

          {/* Generated files summary */}
          {previewPages.length > 0 && (
            <div className="border-t border-[var(--orbis-s2)]" style={{ padding: '12px 20px' }}>
              <p className="text-xs text-[var(--orbis-t4)] uppercase tracking-wider mb-2">Generated Files</p>
              {previewPages.map((p) => (
                <div key={p.slug} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <FileCode className="w-3.5 h-3.5 text-[var(--orbis-t4)]" />
                    <span className="text-xs text-[var(--orbis-t3)]">
                      {p.slug === 'home' ? 'index' : p.slug}.html
                    </span>
                  </div>
                  <span className="text-xs text-[var(--orbis-t4)]">
                    {((p.html?.length || 0) / 1024).toFixed(1)} KB
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ═══ Right panel — Live Preview ═══ */}
        <div className="flex-1 flex flex-col bg-[var(--orbis-s1)]/30 min-w-0">
          {/* Preview toolbar */}
          {previewPages.length > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--orbis-s2)] bg-[var(--orbis-bg)]/50">
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-[var(--orbis-t4)]" />
                <span className="text-xs text-[var(--orbis-t4)]">Live Preview</span>
                {previewPages.length > 1 && (
                  <div className="flex items-center gap-1 ml-3">
                    {previewPages.map((p) => (
                      <button
                        key={p.slug}
                        onClick={() => setCurrentPreviewSlug(p.slug)}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          currentPreviewSlug === p.slug
                            ? 'bg-[var(--orbis-blue)] text-[var(--orbis-bg)]'
                            : 'text-[var(--orbis-t3)] hover:bg-[var(--orbis-s2)]'
                        }`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <ViewportToggle viewport={viewport} onViewportChange={setViewport} />
            </div>
          )}

          {/* Preview iframe or skeleton */}
          {previewSrc ? (
            <div className="flex-1 flex items-start justify-center overflow-hidden bg-[var(--orbis-bg)]/30 p-2">
              <div className={`bg-white overflow-hidden rounded-lg transition-all duration-300 ${
                viewport === 'mobile' ? 'w-[375px] max-h-[812px] h-full' :
                viewport === 'tablet' ? 'w-[768px] max-h-[1024px] h-full' :
                'w-full h-full'
              }`}>
                <iframe
                  srcDoc={previewSrc}
                  className="w-full h-full bg-white"
                  sandbox="allow-scripts allow-same-origin"
                  title="Live Preview"
                />
              </div>
            </div>
          ) : (
            <PreviewSkeleton />
          )}
        </div>
      </div>

      {/* ═══ Bottom bar ═══ */}
      <div className="magic-bottom-bar">
        <div className="flex items-center gap-2">
          {error && (
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {!error && !isComplete && (
            <span style={{ fontSize: 11, color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)', letterSpacing: '0.5px' }}>
              Build · Step 5 of 7
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {error && (
            <button
              onClick={() => { setHasStarted(false); setUserPinned(false); setActiveTab('setup'); setTimeout(() => { setHasStarted(true); startBuild(); }, 0); }}
              className="flex items-center gap-2 rounded-full transition-colors"
              style={{ height: 44, padding: '0 24px', background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)', fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}
            >
              <RefreshCw className="w-4 h-4" />
              Retry Build
            </button>
          )}
          {isComplete && (
            <div className="orbis-cta-ring rounded-full p-0.5">
              <button
                onClick={onComplete}
                className="orbis-cta flex items-center gap-2 rounded-full"
                style={{ height: 44, padding: '0 28px', fontSize: 12, fontWeight: 500, fontFamily: 'var(--orbis-font)' }}
                data-testid="review-website-btn"
              >
                Review Website <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
